import {
  MailOutlined,
  PhoneOutlined,
  PushpinOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Card, Empty, Flex, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCustomerActivities, createCustomerNote, createCustomerCall, createCustomerEmail, createCustomerText } from 'api/customers';
import { createCustomerTask } from 'api/tasks';
import { WL_CONFIG } from 'constants';
import { PERMISSIONS } from 'constants/permissions';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import { getData, priceTransformer } from 'utils';
import { captureException } from 'utils/errors';
import { App } from 'antd';
import AddNoteDrawer from './AddNoteDrawer';
import AddCallDrawer from './AddCallDrawer';
import AddTaskDrawer from './AddTaskDrawer';
import ActivityTimeline from './ActivityTimeline';
import { RunFlowModal } from 'containers/Flows';
import SendEmailModal from './SendEmailModal';
import SendTextModal from './SendTextModal';
import SnapshotPanel from './SnapshotPanel';
import { StyledActivityWrapper } from './styles';

const PAGE_SIZE = 20;

function ActivityTab({ customer }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [callDrawerOpen, setCallDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [runFlowOpen, setRunFlowOpen] = useState(false);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const canCreateTasks = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);

  const fetchActivities = useCallback((p = 1) => {
    if (!customer?.id) return;
    if (p === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    getCustomerActivities(customer.id, { page: p, page_size: PAGE_SIZE })
      .then((res) => {
        const data = getData(res);
        const results = data?.data?.results ?? data?.results ?? [];
        const count = data?.data?.count ?? data?.count ?? 0;
        const items = Array.isArray(results) ? results : [];
        setActivities((prev) => (p === 1 ? items : [...prev, ...items]));
        setTotal(count);
        setPage(p);
      })
      .catch((e) => {
        captureException(e);
        message.error('Failed to load activities');
      })
      .finally(() => {
        if (p === 1) setLoading(false);
        else setLoadingMore(false);
      });
  }, [customer?.id, message]);

  React.useEffect(() => {
    fetchActivities(1);
  }, [fetchActivities]);

  const handleNoteCreated = () => {
    setNoteDrawerOpen(false);
    fetchActivities(1);
    message.success('Note added');
  };

  const handleCallCreated = () => {
    setCallDrawerOpen(false);
    fetchActivities(1);
    message.success('Call logged');
  };

  const handleTaskCreated = () => {
    setTaskDrawerOpen(false);
    fetchActivities(1);
    message.success('Task created');
  };

  const handleRunFlow = () => {
    setRunFlowOpen(true);
  };

  const handleEmailSent = () => {
    setEmailModalOpen(false);
    fetchActivities(1);
    message.success('Email sent');
  };

  const handleTextSent = () => {
    setTextModalOpen(false);
    fetchActivities(1);
    message.success('Text sent');
  };

  if (!customer) return null;

  const primaryAddress = customer?.customer_addresses?.length > 0
    ? customer.customer_addresses[customer.customer_addresses.length - 1]
    : null;
  const addressDisplay = primaryAddress
    ? [primaryAddress.address, primaryAddress.city, primaryAddress.state, primaryAddress.zip_code]
      .filter(Boolean)
      .join(', ')
    : '-';

  return (
    <StyledActivityWrapper>
      <div className="activity-actions">
        <Space wrap>
          <Button type="primary" onClick={() => setNoteDrawerOpen(true)}>
            Add Note
          </Button>
          <Button onClick={() => setCallDrawerOpen(true)}>Add Call</Button>
          {canCreateTasks ? (
            <Button onClick={() => setTaskDrawerOpen(true)}>Create Task</Button>
          ) : null}
          <Button onClick={() => setEmailModalOpen(true)}>Email</Button>
          <Button onClick={() => setTextModalOpen(true)}>Text</Button>
          <Button onClick={handleRunFlow}>Run Flow</Button>
        </Space>
      </div>

      <div className="activity-layout">
        <div className="activity-snapshot">
          <SnapshotPanel
            customer={customer}
            addressDisplay={addressDisplay}
            onCompanyClick={() => navigate(`/console/customers/companies/${customer.company_detail?.id}`)}
          />
        </div>
        <div className="activity-timeline">
          <Card title="Activity" className="timeline-card">
            {loading ? (
              <Flex justify="center" align="center" style={{ minHeight: 200 }}>
                <Spin size="large" />
              </Flex>
            ) : activities.length === 0 ? (
              <Empty description="No activity yet" />
            ) : (
              <ActivityTimeline
                activities={activities}
                onLoadMore={total > page * PAGE_SIZE ? () => fetchActivities(page + 1) : null}
                loadingMore={loadingMore}
                hasReachedEnd={total > 0 && total <= page * PAGE_SIZE}
              />
            )}
          </Card>
        </div>
      </div>

      <AddNoteDrawer
        open={noteDrawerOpen}
        onClose={() => setNoteDrawerOpen(false)}
        onSubmit={(data) => createCustomerNote(customer.id, data).then(handleNoteCreated)}
      />
      <AddCallDrawer
        open={callDrawerOpen}
        onClose={() => setCallDrawerOpen(false)}
        onSubmit={(data) => createCustomerCall(customer.id, data).then(handleCallCreated)}
      />
      <AddTaskDrawer
        open={taskDrawerOpen}
        onClose={() => setTaskDrawerOpen(false)}
        onSubmit={(data) => createCustomerTask(customer.id, data).then(handleTaskCreated)}
      />
      <SendEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={(data) => createCustomerEmail(customer.id, data).then(handleEmailSent)}
        defaultToEmail={customer?.email}
      />
      <SendTextModal
        open={textModalOpen}
        onClose={() => setTextModalOpen(false)}
        onSubmit={(data) => createCustomerText(customer.id, data).then(handleTextSent)}
        defaultToPhone={customer?.phone}
      />
      <RunFlowModal
        open={runFlowOpen}
        onClose={() => setRunFlowOpen(false)}
        onSuccess={() => fetchActivities(1)}
        customerId={customer?.id}
      />
    </StyledActivityWrapper>
  );
}

export default ActivityTab;
